import type { EngineFactEnvelope } from "../report/contracts";
import { translateCanChi } from "./can-chi";
import { guardVietnameseDisplayText } from "./text-guard";
import type { VietnameseDisplayFact } from "./types";

const FALLBACK_VALUE_VI = "Chưa thể hiển thị dữ liệu này";
const FALLBACK_LABEL_VI = "Dữ kiện kỹ thuật";

export function asRecord(value: unknown): Record<string, unknown> | null {
  return value !== null && typeof value === "object"
    ? (value as Record<string, unknown>)
    : null;
}

export function asString(value: unknown): string {
  return typeof value === "string" || typeof value === "number"
    ? String(value)
    : "";
}

function safeFact(
  id: string,
  labelVi: string,
  valueVi: string,
  citationIds: readonly string[],
  explanationVi?: string,
): VietnameseDisplayFact {
  const label = guardVietnameseDisplayText(labelVi, FALLBACK_LABEL_VI);
  const value = guardVietnameseDisplayText(valueVi, FALLBACK_VALUE_VI);
  const explanation =
    explanationVi === undefined
      ? undefined
      : guardVietnameseDisplayText(explanationVi, "").textVi || undefined;

  return Object.freeze({
    id,
    labelVi: label.textVi,
    valueVi: value.textVi,
    explanationVi: explanation,
    citationIds,
  });
}

const pillarLabelVi: Readonly<Record<string, string>> = Object.freeze({
  year: "Trụ năm",
  month: "Trụ tháng",
  day: "Trụ ngày",
  hour: "Trụ giờ",
});

export function baziDisplayFacts(
  envelope: EngineFactEnvelope,
): readonly VietnameseDisplayFact[] {
  const pillars = Array.isArray(envelope.facts.pillars)
    ? envelope.facts.pillars
    : [];

  return Object.freeze(
    pillars.map((pillar) => {
      const item = asRecord(pillar);
      const key = asString(item?.key);
      const stem = asString(item?.stem);
      const branch = asString(item?.branch);
      const translated = translateCanChi(stem, branch);

      return safeFact(
        `bazi.pillar.${key}`,
        pillarLabelVi[key] ?? "Trụ",
        translated.textVi,
        envelope.sourceRefs,
        translated.explanationVi,
      );
    }),
  );
}

function starNames(value: unknown): readonly string[] {
  return Array.isArray(value) ? value.map(asString).filter(Boolean) : [];
}

export function ziweiDisplayFacts(
  envelope: EngineFactEnvelope,
): readonly VietnameseDisplayFact[] {
  const palaces = Array.isArray(envelope.facts.palaces)
    ? envelope.facts.palaces
    : [];

  const palaceFacts = palaces.map((palace) => {
    const item = asRecord(palace);
    const name = asString(item?.name) || "Cung chưa xác định";
    const stem = asString(item?.heavenlyStem);
    const branch = asString(item?.earthlyBranch);
    const canChi = translateCanChi(stem, branch);
    const isBodyPalace = item?.isBodyPalace === true;
    const label = isBodyPalace ? `${name} (Cung an Thân)` : name;
    const majorStars = starNames(item?.majorStars);
    const minorStars = starNames(item?.minorStars);
    const adjectiveStars = starNames(item?.adjectiveStars);
    const valueVi = [
      `Chính tinh: ${majorStars.join(" · ") || "không ghi nhận"}`,
      `Can chi: ${canChi.textVi}`,
      `Phụ tinh: ${minorStars.join(" · ") || "không ghi nhận"}`,
      `Sao biến hóa: ${adjectiveStars.join(" · ") || "không ghi nhận"}`,
    ].join("; ");

    return safeFact(
      `ziwei.palace.${asString(item?.index)}`,
      label,
      valueVi,
      envelope.sourceRefs,
    );
  });

  const zodiac = asString(envelope.facts.zodiac);
  const fiveElementsClass = asString(envelope.facts.fiveElementsClass);

  const summaryFacts = [
    zodiac
      ? safeFact("ziwei.zodiac", "Con giáp", zodiac, envelope.sourceRefs)
      : null,
    fiveElementsClass
      ? safeFact(
          "ziwei.five-elements-class",
          "Cục Ngũ Hành",
          fiveElementsClass,
          envelope.sourceRefs,
        )
      : null,
  ].filter((fact): fact is VietnameseDisplayFact => fact !== null);

  return Object.freeze([...summaryFacts, ...palaceFacts]);
}

function hexagramLabel(
  hexagram: string,
  hexagramName: string,
  upperTrigram: string,
  lowerTrigram: string,
): string {
  if (!hexagram || !hexagramName) return "—";
  const isPure = upperTrigram !== "" && upperTrigram === lowerTrigram;
  return `Quẻ ${hexagram} — ${hexagramName}${isPure ? ` (Tập ${hexagramName})` : ""}`;
}

export function ichingDisplayFacts(
  envelope: EngineFactEnvelope,
): readonly VietnameseDisplayFact[] {
  const primaryHexagram = asString(envelope.facts.primaryHexagram);
  const primaryHexagramName = asString(envelope.facts.primaryHexagramName);
  const changingLine = asString(envelope.facts.changingLine);
  const transformedHexagram = asString(envelope.facts.transformedHexagram);
  const transformedHexagramName = asString(
    envelope.facts.transformedHexagramName,
  );
  const upperTrigram = asString(envelope.facts.upperTrigram);
  const upperTrigramName = asString(envelope.facts.upperTrigramName);
  const lowerTrigram = asString(envelope.facts.lowerTrigram);
  const lowerTrigramName = asString(envelope.facts.lowerTrigramName);
  const transformedUpperTrigram = asString(
    envelope.facts.transformedUpperTrigram,
  );
  const transformedUpperTrigramName = asString(
    envelope.facts.transformedUpperTrigramName,
  );
  const transformedLowerTrigram = asString(
    envelope.facts.transformedLowerTrigram,
  );
  const transformedLowerTrigramName = asString(
    envelope.facts.transformedLowerTrigramName,
  );

  return Object.freeze([
    safeFact(
      "iching.primary-hexagram",
      "Quẻ chủ",
      hexagramLabel(
        primaryHexagram,
        primaryHexagramName,
        upperTrigram,
        lowerTrigram,
      ),
      envelope.sourceRefs,
    ),
    safeFact(
      "iching.changing-line",
      "Hào động",
      changingLine ? `Hào ${changingLine}` : "—",
      envelope.sourceRefs,
    ),
    safeFact(
      "iching.transformed-hexagram",
      "Quẻ biến",
      hexagramLabel(
        transformedHexagram,
        transformedHexagramName,
        transformedUpperTrigram,
        transformedLowerTrigram,
      ),
      envelope.sourceRefs,
    ),
    safeFact(
      "iching.trigrams",
      "Thượng quái / Hạ quái",
      upperTrigramName && lowerTrigramName
        ? `${upperTrigramName} / ${lowerTrigramName}`
        : "—",
      envelope.sourceRefs,
    ),
    safeFact(
      "iching.transformed-trigrams",
      "Thượng quái biến / Hạ quái biến",
      transformedUpperTrigramName && transformedLowerTrigramName
        ? `${transformedUpperTrigramName} / ${transformedLowerTrigramName}`
        : "—",
      envelope.sourceRefs,
      "Số thứ tự bát quái sau khi đổi hào động.",
    ),
  ]);
}

const pythagorasFieldLabelVi: Readonly<Record<string, string>> = Object.freeze({
  lifePath: "Số đường đời",
  birthdayNumber: "Số ngày sinh",
  attitudeNumber: "Số thái độ",
  expressionNumber: "Số biểu đạt",
  soulUrgeNumber: "Số linh hồn",
  personalityNumber: "Số nhân cách",
});

export function pythagorasDisplayFacts(
  envelope: EngineFactEnvelope,
): readonly VietnameseDisplayFact[] {
  const fields = Object.keys(pythagorasFieldLabelVi);

  return Object.freeze(
    fields.map((field) =>
      safeFact(
        `pythagoras.${field}`,
        pythagorasFieldLabelVi[field],
        asString(envelope.facts[field]) || "—",
        envelope.sourceRefs,
      ),
    ),
  );
}

function directionListVi(value: unknown): string {
  if (!Array.isArray(value)) return "—";
  const items = value
    .map((entry) => asRecord(entry))
    .filter((entry): entry is Record<string, unknown> => entry !== null)
    .map(
      (entry) => `${asString(entry.tenHuongVi)} (${asString(entry.tenSaoVi)})`,
    );
  return items.length > 0 ? items.join(", ") : "—";
}

export function fengShuiDisplayFacts(
  envelope: EngineFactEnvelope,
): readonly VietnameseDisplayFact[] {
  const batTrach = asRecord(envelope.facts.batTrach);
  const huyenKhong = asRecord(envelope.facts.huyenKhong);
  const facts: VietnameseDisplayFact[] = [];

  facts.push(
    safeFact(
      "feng-shui.huong-nha",
      "Hướng nhà",
      asString(envelope.facts.huongNhaVi) || "—",
      envelope.sourceRefs,
    ),
  );

  if (batTrach) {
    facts.push(
      safeFact(
        "feng-shui.bat-trach.menh",
        "Bát Trạch — Quẻ mệnh",
        `${asString(batTrach.quaiMenhVi)} (Kua ${asString(batTrach.kuaSoVi)}, ${asString(batTrach.nhomMenhVi)})`,
        envelope.sourceRefs,
      ),
      safeFact(
        "feng-shui.bat-trach.danh-gia",
        "Bát Trạch — Đánh giá hướng hiện tại",
        asString(batTrach.danhGiaHuongHienTaiVi) || "—",
        envelope.sourceRefs,
        asString(batTrach.khuyenNghiVi) || undefined,
      ),
      safeFact(
        "feng-shui.bat-trach.huong-tot",
        "Bát Trạch — 4 hướng tốt",
        directionListVi(batTrach.huongTotVi),
        envelope.sourceRefs,
      ),
      safeFact(
        "feng-shui.bat-trach.huong-xau",
        "Bát Trạch — 4 hướng xấu",
        directionListVi(batTrach.huongXauVi),
        envelope.sourceRefs,
      ),
    );
  }

  if (huyenKhong) {
    facts.push(
      safeFact(
        "feng-shui.huyen-khong.van",
        "Huyền Không Phi Tinh — Vận",
        asString(huyenKhong.nguyenVanVi) || "—",
        envelope.sourceRefs,
      ),
      safeFact(
        "feng-shui.huyen-khong.to-hop",
        "Huyền Không Phi Tinh — Tổ hợp sao",
        `${asString(huyenKhong.toHopVi)} (Sơn ${asString(huyenKhong.saoToaVi)} · Hướng ${asString(huyenKhong.saoHuongVi)})`,
        envelope.sourceRefs,
        asString(huyenKhong.khuyenNghiVi) || undefined,
      ),
    );
  }

  return Object.freeze(facts);
}

export function nameWuxingDisplayFacts(
  envelope: EngineFactEnvelope,
): readonly VietnameseDisplayFact[] {
  const hanTruyenThong = asRecord(envelope.facts.hanTruyenThong);
  const thanSoTay = asRecord(envelope.facts.thanSoTay);
  const facts: VietnameseDisplayFact[] = [];

  if (hanTruyenThong) {
    if (hanTruyenThong.status === "ok") {
      facts.push(
        safeFact(
          "name-wuxing.han-truyen-thong.nguhanh",
          "Hán truyền thống — Ngũ Hành của tên",
          asString(hanTruyenThong.nguHanhTongHopVi) || "—",
          envelope.sourceRefs,
          asString(hanTruyenThong.khuyenNghiVi) || undefined,
        ),
      );
    } else {
      facts.push(
        safeFact(
          "name-wuxing.han-truyen-thong.blocked",
          "Hán truyền thống",
          asString(hanTruyenThong.reasonVi) || "Không tính được.",
          envelope.sourceRefs,
        ),
      );
    }
  }

  if (thanSoTay) {
    facts.push(
      safeFact(
        "name-wuxing.than-so-tay.duong-doi",
        "Thần số học — Số Đường Đời",
        `${asString(thanSoTay.soDuongDoiVi)} (${asString(thanSoTay.nguHanhDuongDoiVi)})`,
        envelope.sourceRefs,
      ),
      safeFact(
        "name-wuxing.than-so-tay.dien-thoai",
        "Thần số học — Số điện thoại",
        `${asString(thanSoTay.soDienThoaiRutGonVi)} (${asString(thanSoTay.nguHanhDienThoaiVi)})`,
        envelope.sourceRefs,
      ),
      safeFact(
        "name-wuxing.than-so-tay.quan-he",
        "Thần số học — Quan hệ Ngũ Hành",
        asString(thanSoTay.quanHeVi) || "—",
        envelope.sourceRefs,
        asString(thanSoTay.khuyenNghiVi) || undefined,
      ),
    );
  }

  return Object.freeze(facts);
}

export function engineDisplayFacts(
  envelope: EngineFactEnvelope,
): readonly VietnameseDisplayFact[] {
  switch (envelope.engine) {
    case "bazi":
      return baziDisplayFacts(envelope);
    case "ziwei":
      return ziweiDisplayFacts(envelope);
    case "iching":
      return ichingDisplayFacts(envelope);
    case "pythagoras":
      return pythagorasDisplayFacts(envelope);
    case "feng_shui":
      return fengShuiDisplayFacts(envelope);
    case "name_phone_wuxing":
      return nameWuxingDisplayFacts(envelope);
    default:
      return Object.freeze([]);
  }
}
